import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MessageDisplayType, ErrorStackDataType } from "@preveil-api";
import { FSMessage, FSStatus } from "src/common/keys/protos/collections_pb";
import { useAppDispatch, Message, MessageHandlerDisplayType, DriveErrorMessages, useAppSelector, DriveRequest, filesyncSocketApi, ErrorStackItem, getEnumKey, PrivateRoutes } from "src/common";
import { uiActions, websocketActions, MessageRequest, WebsocketStatus } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

export function useWatchSocket() {
  const current_location = useLocation();
  const callbacks = useAppSelector((state: RootState) => state.websocket.watch_ws_callbacks);
  const ws_status = useAppSelector((state: RootState) => state.websocket.drive_watch_ws_status);
  const [initSocket, { data, error }] = filesyncSocketApi.useLazyInitializeWatchHooksQuery();
  const dispatch = useAppDispatch();

  useEffect(() => {
    // NOTE: Initialize the watched drive socket connection on navigating to the component
    // Pass a timestamp to rebind the  onCacheEntryAdded otherwise it is cached
    const open_socket = current_location.pathname.includes(PrivateRoutes.drive_route);
    (open_socket && ws_status === WebsocketStatus.not_initialized || ws_status === WebsocketStatus.disconnected) &&
      initSocket(new Date().getTime().toString());
  }, [ws_status, current_location]);

  useEffect(() => {
    if (!!data && data.length > 0) {
      _.forEach(data, (request: MessageRequest) => {
        (!!request.id && request.type === FSMessage.Type.RESPONSE) && matchRequestResponse(request);
      });
    }

    !!error &&
      handlePageErrorMessage(DriveErrorMessages.default, { stack_message: "error through drive websocket", stack_error: error }, null, MessageHandlerDisplayType.toastr);
  }, [data, error]);

  // Description: Matches the request message to a response callback function
  function matchRequestResponse(data: MessageRequest) {
    const request_id = data.id;
    const message = data.message;
    const message_callback = _.find(callbacks, (request: DriveRequest) => request.request_id.B64() === request_id);
    if (!!request_id && !!message && !!message_callback && message_callback.callback instanceof Function) {
      message_callback.callback(message);
      removeCallback(request_id);
    } else {
      const stack = {
        stack_message: DriveErrorMessages.no_request_match_found,
        stack_error: { request_id, message, message_callback }
      };
      handlePageErrorMessage(DriveErrorMessages.default, stack);
    }
  }

  // Description: Remove callback from store after consuming it
  function removeCallback(request_id: string) {
    const new_callback = callbacks.slice();
    _.remove(new_callback, (request: DriveRequest) => request_id === request.request_id.B64());
    dispatch(websocketActions.setWsDriveWatchCallback(new_callback));
    invalidateCache();
  }

  // Description: remove cache after managing the MessageRequest and matching them to Callbacks 
  function invalidateCache() {
    dispatch(filesyncSocketApi.util.invalidateTags(["watch_ws"]));
  }

  // Description: Handle error message and send error to store
  function handlePageErrorMessage(message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null,
    display_type: MessageDisplayType = MessageHandlerDisplayType.logger) {
    const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
    const stack = new ErrorStackItem("[useWatchSocket Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
    dispatch(uiActions.handleRequestErrors(new Message(message, display_type), stack));
  }

  return {
    data,
    error
  };
}